import {
  MBR_TRAIN_ASSET,
  PUMP_ASSET,
  VALVE_ASSET,
  WATER_TOWER_ASSET,
  WET_WELL_ASSET,
} from 'src/variables';
import {
  BOOSTER_PUMP_STATION,
  COMBINED_SEWAGE_OVERFLOW,
  LIFT_STATION,
  PROCESS_PLANT,
  WASTEWATER_TREATMENT_PLANT,
  WATER_TOWER,
  WATER_TREATMENT_PLANT,
  WATER_WELL,
} from 'src/variables/siteTypes';
import { Option } from 'src/features/formRenderer/types';
import { AssetReportAppliedFilters, ReportTemplateDesign } from '../types';
import { REPORT_TEMPLATE_TYPE } from './pdfReportConfig';

const NO_DATE_LIMIT_AGGREGATION_INTERVALS = ['THIRTY_MIN', 'HOURLY', 'DAILY'];

const TABLE_HEAD_REPORTS = [
  { id: 'siteName', label: 'Site Name', align: 'left', testId: 'report-site-name-header' },
  {
    id: 'asset',
    label: 'Asset',
    align: 'left',
    testId: 'report-asset-header',
  },
  {
    id: 'dailyRuntime',
    label: 'Daily Runtime[Hours]',
    align: 'left',
    testId: 'report-daily-runtime-header',
  },
  {
    id: 'dailyCycles',
    label: 'Daily Cycles',
    align: 'left',
    testId: 'report-daily-cycles-header',
  },
  {
    id: 'dailyVolumetricFlow',
    label: 'Daily Volumetric Flow',
    align: 'left',
    testId: 'report-daily-volumetric-flow-header',
  },
  {
    id: 'averageDailyEfficiency',
    label: 'Average Daily Efficiency',
    align: 'left',
    testId: 'report-average-daily-efficiency-header',
  },
  {
    id: 'totalDailyEnergyConsumption',
    label: 'Total Daily Energy Consumption',
    align: 'left',
    testId: 'report-total-daily-energy-consumption-header',
  },
];

const TABLE_HEAD_REPORTS_NEW = [
  { id: 'type', label: 'Type', align: 'left', testId: 'report-type-header' },
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    testId: 'report-name-header',
  },
  {
    id: 'createdBy',
    label: 'Created By',
    align: 'left',
    testId: 'report-created-by-header',
  },
  {
    id: 'modified',
    label: 'Modified',
    align: 'left',
    testId: 'report-modified-header',
  },
  {
    id: 'favorite',
    label: 'Favorite',
    align: 'left',
    testId: 'report-favorite-header',
  },
  { id: '', testId: 'context-menu-header' },
];

const ASSET_REPORTS_FILTER_CATEGORIES = [
  {
    stateName: 'siteTypes',
    type: 'Site Type',
    options: [
      { label: 'Lift Station', type: LIFT_STATION },
      { label: 'Wastewater Treatment Plant ', type: WASTEWATER_TREATMENT_PLANT },
      { label: 'Water Treatment Plant', type: WATER_TREATMENT_PLANT },
      { label: 'Booster Pump Station', type: BOOSTER_PUMP_STATION },
      { label: 'Water Tower', type: WATER_TOWER },
      { label: 'Process Plant', type: PROCESS_PLANT },
      { label: 'Combined Sewage Overflow', type: COMBINED_SEWAGE_OVERFLOW },
      { label: 'Water Well', type: WATER_WELL },
    ],
  },
  {
    stateName: 'assetTypes',
    type: 'Asset Type',
    options: [
      { label: 'Pump', type: PUMP_ASSET },
      { label: 'Water Tower', type: WATER_TOWER_ASSET },
      { label: 'Wet Well', type: WET_WELL_ASSET },
      { label: 'MBR Train', type: MBR_TRAIN_ASSET },
      { label: 'Valve', type: VALVE_ASSET },
    ],
  },
] as const;

const TIME_FRAME_OPTIONS = [
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_365_days', label: 'Last 365 Days' },
];

const TABLE_HEAD_REPORTS_HISTORY = [
  { id: 'type', label: 'Type', align: 'left', testId: 'report-type-header' },
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    testId: 'report-name-header',
  },
  {
    id: 'createdBy',
    label: 'Created By',
    align: 'left',
    testId: 'report-created-by-header',
  },
  {
    id: 'created',
    label: 'Created',
    align: 'left',
    testId: 'report-created-header',
  },
  {
    id: 'status',
    label: 'Status',
    align: 'left',
    testId: 'report-status-header',
  },
  {
    id: 'aiSummary',
    label: 'AI Summary',
    align: 'left',
    testId: 'report-ai-summary-header',
  },
  {
    id: 'cancel',
    label: '',
    align: 'left',
    testId: 'cancel-header',
  },
  { id: '', testId: 'context-menu-header' },
];

const DATA_AGGREGATION_OPTIONS = [
  { value: 'NONE', label: 'No Aggregation' },
  { value: 'ONE_MIN', label: '1min' },
  { value: 'FIVE_MIN', label: '5min' },
  { value: 'TEN_MIN', label: '10min' },
  { value: 'FIFTEEN_MIN', label: '15min' },
  { value: 'THIRTY_MIN', label: '30min' },
  { value: 'HOURLY', label: '1h' },
  { value: 'DAILY', label: 'Daily' },
];

const ASSET_AGGREGATION_OPTIONS = [
  { value: 'ONE_MIN', label: '1min' },
  { value: 'FIVE_MIN', label: '5min' },
  { value: 'TEN_MIN', label: '10min' },
  { value: 'FIFTEEN_MIN', label: '15min' },
  { value: 'THIRTY_MIN', label: '30min' },
  { value: 'HOURLY', label: '1h' },
  { value: 'DAILY', label: 'Daily' },
];

const COLUMN_STATISTICS = [
  {
    value: 'TOTAL',
    label: 'Total',
  },
  {
    value: 'MIN',
    label: 'Minimum',
  },
  {
    value: 'AVERAGE',
    label: 'Average',
  },
  {
    value: 'MAX',
    label: 'Maximum',
  },
];

const ASSET_REPORT_DEFAULT_COLUMNS = [
  // {
  //   name: 'date',
  //   label: 'Date',
  // },
  {
    name: 'site_name',
    label: 'Site Name',
  },
  {
    name: 'site_type',
    label: 'Site Type',
  },
  {
    name: 'asset_name',
    label: 'Asset Name',
  },
  {
    name: 'asset_type',
    label: 'Asset Type',
  },
];

const AGGREGATION_SELECT_OPTIONS: Omit<Option, 'order'>[] = [
  {
    value: 'total',
    label: 'Total',
  },
  {
    value: 'average',
    label: 'Average',
  },
  {
    value: 'min',
    label: 'Min',
  },
  {
    value: 'max',
    label: 'Max',
  },
];
const DATA_REPORT_DEFAULT_COLUMNS = [
  // {
  //   name: 'date',
  //   label: 'Date',
  // },
  // {
  //   name: 'time',
  //   label: 'Time',
  // },
];

const SEPARATOR = '|';
const ASSET = 'asset';
const SITE = 'site';

const SYSTEM = 'System';
const MIGRATION = 'Migration';
const USER = 'User';
const SCHEDULER = 'SchedulerSystem';

const initialFilters: AssetReportAppliedFilters = {
  selectedItems: [],
  assetTypes: [PUMP_ASSET, WATER_TOWER_ASSET, WET_WELL_ASSET, MBR_TRAIN_ASSET, VALVE_ASSET],
  sitesTypes: [
    LIFT_STATION,
    WASTEWATER_TREATMENT_PLANT,
    WATER_TREATMENT_PLANT,
    BOOSTER_PUMP_STATION,
    WATER_TOWER,
    PROCESS_PLANT,
    COMBINED_SEWAGE_OVERFLOW,
    WATER_WELL,
  ],
};
const ASSET_REPORT_INITIAL_STATE = {
  header: {
    reportType: '',
    timeZone: '',
  },
  filters: initialFilters,
  data: {
    timeFrame: 'this_week',
    aggregationInterval: 'DAILY',
    generateAiSummary: false,
    columns: {
      default: {
        date: false,
        site_name: false,
        site_type: false,
        asset_name: false,
        asset_type: false,
      },
      custom: [],
    },
    timeZone: '',
  },
};
const DATA_REPORT_INITIAL_STATE = {
  header: {
    reportType: '',
    timeZone: '',
  },
  data: {
    timeFrame: 'this_week',
    aggregationInterval: 'DAILY',
    columns: {
      default: {
        date: false,
        time: false,
      },
      custom: [],
    },
    timeZone: '',
  },
};

const PDF_REPORT_INITAL_STATE = {
  header: {
    reportType: 'PDF',
    reportTemplate: '',
    aggregationInterval: 'FIVE_MIN',
  },
  data: {
    timeFrame: 'this_week',
    dataInterval: '60',
    outlierTolerance: '5',
    mlssPerWeek: '',
    assetIds: [],
    columns: {
      custom: [],
    },
    reportTemplate: '',
    siteId: '',
    timeZone: '',
  },
};
const DEFAULT_REPORT_DESIGN: ReportTemplateDesign = { showStreametricLogo: true };

const PDF_SITE_STATUS_REPORT_INITIAL_STATE = {
  header: {
    reportType: 'PDF_SITE_STATUS',
    reportTemplate: REPORT_TEMPLATE_TYPE.SITE_STATUS_REPORT,
    aggregationInterval: 'NONE',
  },
  data: {
    timeFrame: 'last_30_days',
    tolerance: '',
    tagList: [],
    siteId: '',
    timeZone: '',
    design: { ...DEFAULT_REPORT_DESIGN },
  },
};

const PDF_REPORT_INITIAL_STATE_POPULATED = {
  header: {
    reportType: 'PDF',
    reportTemplate: '',
    aggregationInterval: 'FIVE_MIN',
  },
  data: {
    timeFrame: 'this_week',
    dataInterval: '60',
    outlierTolerance: '5',
    mlssPerWeek: '',
    tolerance: '0',
    assetIds: [],
    columns: {
      custom: [],
    },
    siteId: '',
    timeZone: '',
    // populated: true,
  },
};
const PDF_SITE_STATUS_REPORT_INITIAL_STATE_POPULATED = {
  header: {
    reportType: 'PDF_SITE_STATUS',
    reportTemplate: { label: 'Site Status Report', value: REPORT_TEMPLATE_TYPE.SITE_STATUS_REPORT },
    aggregationInterval: 'NONE',
  },
  data: {
    timeFrame: 'last_30_days',
    tolerance: '0',
    tagList: [],
    siteId: '',
    timeZone: '',
    design: { ...DEFAULT_REPORT_DESIGN },
  },
};

const ASSET_REPORT_INITIAL_STATE_POPULATED = {
  header: {
    reportType: '',
    timeZone: '',
  },
  filters: initialFilters,
  data: {
    timeFrame: 'this_week',
    aggregationInterval: 'DAILY',
    generateAiSummary: false,
    columns: {
      default: {
        date: true,
        site_name: true,
        site_type: true,
        asset_name: true,
        asset_type: true,
      },
      custom: [],
    },
    timeZone: '',
  },
};
const DATA_REPORT_INITIAL_STATE_POPULATED = {
  header: {
    reportType: '',
    timeZone: '',
  },
  data: {
    timeFrame: 'this_week',
    aggregationInterval: 'DAILY',
    columns: {
      default: {
        date: true,
        time: true,
      },
      custom: [],
    },
    timeZone: '',
  },
};

const DATA_REPORT_STRING_NAME = 'data-reports';
const PDF_REPORT_STRING_NAME = 'pdf-reports';
const ASSET_REPORT_STRING_NAME = 'asset-reports';
export {
  DATA_AGGREGATION_OPTIONS,
  ASSET_AGGREGATION_OPTIONS,
  COLUMN_STATISTICS,
  DATA_REPORT_INITIAL_STATE,
  PDF_REPORT_INITAL_STATE,
  DATA_REPORT_INITIAL_STATE_POPULATED,
  AGGREGATION_SELECT_OPTIONS,
  ASSET_REPORT_DEFAULT_COLUMNS,
  DATA_REPORT_DEFAULT_COLUMNS,
  TABLE_HEAD_REPORTS_HISTORY,
  TABLE_HEAD_REPORTS,
  TABLE_HEAD_REPORTS_NEW,
  ASSET_REPORT_INITIAL_STATE,
  ASSET_REPORT_INITIAL_STATE_POPULATED,
  PDF_REPORT_INITIAL_STATE_POPULATED,
  ASSET_REPORTS_FILTER_CATEGORIES,
  TIME_FRAME_OPTIONS,
  SEPARATOR,
  ASSET,
  SITE,
  SYSTEM,
  MIGRATION,
  USER,
  SCHEDULER,
  initialFilters,
  DATA_REPORT_STRING_NAME,
  PDF_REPORT_STRING_NAME,
  ASSET_REPORT_STRING_NAME,
  PDF_SITE_STATUS_REPORT_INITIAL_STATE,
  PDF_SITE_STATUS_REPORT_INITIAL_STATE_POPULATED,
  DEFAULT_REPORT_DESIGN,
  NO_DATE_LIMIT_AGGREGATION_INTERVALS,
};
